/**
 * System overview — the admin/owner landing dashboard.
 *
 * One screen that answers "is everything healthy right now?":
 *   · KPI tiles (odometer-style) for the headline numbers
 *   · radial gauges for service health + edge freshness
 *   · a 14-day ingest bar chart
 *   · a per-microservice status grid (up/down + latency)
 *   · the endoedge sync verdict (cloud-vs-edge), mirroring
 *     scripts/check-endoedge-sync.sh
 *
 * Data: GET /viewer-api/admin/system-status (admin/owner only),
 * auto-refreshed every 15 s. All charts are inline SVG — no chart
 * library dependency.
 */
import { createMemo, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";

interface ServiceHealth { name: string; up: boolean; status: number; latency_ms: number }
interface SystemStatus {
  generated_at: string;
  ingest: {
    last_study_ingest: string | null; last_instance_commit: string | null;
    studies_ingested_today: number; procedures_today: number; instances_last_hour: number;
    per_day: { day: string; count: number }[];
  };
  edge: {
    last_heartbeat: string | null; heartbeat_age_min: number | null; online: boolean;
    activations: { id: string; version: string; last_seen: string; age_min: number; site: string | null; lic: string | null }[];
    uploads: { box: string; site: string | null; today: number; week: number; last_at: string | null }[];
  };
  auth: { jwks_keys: number; ok: boolean };
  services: ServiceHealth[];
  db_ok: boolean;
}

function authHeader(): Record<string, string> {
  const a = JSON.parse(sessionStorage.getItem("vpd.auth.v1") ?? "null");
  return { Authorization: `Bearer ${a?.accessToken ?? ""}` };
}
async function fetchStatus(): Promise<SystemStatus> {
  const r = await fetch("/viewer-api/admin/system-status", { headers: { Accept: "application/json", ...authHeader() } });
  if (!r.ok) throw new Error(`system-status ${r.status}`);
  return r.json();
}

function fmtAge(min: number | null | undefined): string {
  if (min == null || min < 0) return "—";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ${min % 60}m ago`;
  return `${Math.floor(h / 24)}d ago`;
}
function fmtTs(s: string | null): string {
  if (!s) return "never";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

// ── Radial gauge (odometer): value/max as a 240° arc ──────────────
function Gauge(p: { value: number; max: number; label: string; suffix?: string; tone: "ok" | "warn" | "danger" }) {
  const pct = createMemo(() => p.max > 0 ? Math.max(0, Math.min(1, p.value / p.max)) : 0);
  const R = 52, CX = 60, CY = 60;
  const START = 150, SWEEP = 240;      // degrees; arc opens at the bottom
  const polar = (deg: number) => {
    const r = (deg * Math.PI) / 180;
    return { x: CX + R * Math.cos(r), y: CY + R * Math.sin(r) };
  };
  const arc = (frac: number) => {
    const a0 = START, a1 = START + SWEEP * frac;
    const s = polar(a0), e = polar(a1);
    const large = (a1 - a0) > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${R} ${R} 0 ${large} 1 ${e.x} ${e.y}`;
  };
  const color = () => p.tone === "ok" ? "#27a567" : p.tone === "warn" ? "#d97706" : "#dc2626";
  return (
    <div class="flex flex-col items-center">
      <svg viewBox="0 0 120 120" class="h-28 w-28">
        <path d={arc(1)} fill="none" stroke="currentColor" class="text-ink-300" stroke-width="10" stroke-linecap="round" />
        <path d={arc(pct())} fill="none" stroke={color()} stroke-width="10" stroke-linecap="round" />
        <text x="60" y="58" text-anchor="middle" class="fill-ink-900" font-size="24" font-weight="700">{p.value}</text>
        <text x="60" y="76" text-anchor="middle" class="fill-ink-600" font-size="11">{p.suffix ?? `/ ${p.max}`}</text>
      </svg>
      <span class="mt-0.5 text-[11px] uppercase tracking-wider text-ink-700">{p.label}</span>
    </div>
  );
}

// ── KPI tile (odometer-style big number) ──────────────────────────
function Kpi(p: { label: string; value: string | number; sub?: string; tone?: "ok" | "warn" | "danger" | "neutral" }) {
  const c = p.tone === "ok" ? "text-ok" : p.tone === "warn" ? "text-warn" : p.tone === "danger" ? "text-danger" : "text-ink-900";
  return (
    <div class="flex min-w-[140px] flex-1 flex-col rounded-lg border border-ink-300 bg-ink-100 px-4 py-3">
      <span class="text-[10px] uppercase tracking-wider text-ink-700">{p.label}</span>
      <span class={`mt-1 font-mono text-2xl font-bold tabular-nums ${c}`}>{p.value}</span>
      <Show when={p.sub}><span class="mt-0.5 text-[11px] text-ink-600">{p.sub}</span></Show>
    </div>
  );
}

// ── 14-day ingest bar chart (inline SVG) ──────────────────────────
function IngestBars(p: { data: { day: string; count: number }[] }) {
  const max = createMemo(() => Math.max(1, ...p.data.map((d) => d.count)));
  const W = 28, GAP = 8, H = 120;
  return (
    <div class="overflow-x-auto">
      <svg height={H + 28} width={Math.max(1, p.data.length) * (W + GAP)} class="block">
        <For each={p.data}>{(d, i) => {
          const h = Math.round((d.count / max()) * H);
          const x = i() * (W + GAP);
          return (
            <g>
              <rect x={x} y={H - h} width={W} height={h} rx="3" class="fill-brand" />
              <text x={x + W / 2} y={H - h - 4} text-anchor="middle" class="fill-ink-700" font-size="10">{d.count}</text>
              <text x={x + W / 2} y={H + 14} text-anchor="middle" class="fill-ink-600" font-size="9">{d.day.slice(5)}</text>
            </g>
          );
        }}</For>
      </svg>
    </div>
  );
}

export function SystemOverview() {
  const q = createQuery(() => ({
    queryKey: ["system-status"],
    queryFn:  fetchStatus,
    refetchInterval: 15_000,
  }));
  const s = () => q.data;

  const servicesUp = createMemo(() => (s()?.services ?? []).filter((x) => x.up).length);
  const servicesTotal = createMemo(() => (s()?.services ?? []).length);
  const allUp = createMemo(() => servicesTotal() > 0 && servicesUp() === servicesTotal());

  // Sync verdict. Driven primarily by INGEST recency — studies
  // arriving is proof the edge reaches the cloud. The license
  // heartbeat is only a coarse "has the box been in contact" hint
  // (≈30-day license cadence), so it never asserts "offline" on its
  // own — that produced false alarms during normal between-case gaps.
  const verdict = createMemo(() => {
    const d = s(); if (!d) return null;
    const recent = d.ingest.instances_last_hour > 0 || d.ingest.studies_ingested_today > 0;
    if (!allUp() || !d.auth.ok || !d.db_ok)
      return { tone: "danger", text: "Cloud-side fault — a service / auth / DB check is failing. Fix the cloud first." };
    if (recent)
      return { tone: "ok", text: "Healthy — uploads have arrived recently, so the edge is reaching the cloud and sync is working." };
    return { tone: "warn", text: "No uploads recently. Usually this just means no new cases yet, or the room boxes are idle/off (normal outside clinic hours). If cases are expected, check the endoedge box at the site (app running? network?)." };
  });

  return (
    <div class="flex flex-col gap-4">
      <div class="flex items-center gap-3">
        <h2 class="text-base font-semibold text-ink-900">System overview</h2>
        <Show when={s()}>
          <span class="text-[11px] text-ink-600">refreshed {fmtTs(s()!.generated_at)} · auto every 15s</span>
        </Show>
        <button class="ml-auto btn-ghost !text-[11px]" onClick={() => q.refetch()}>refresh now</button>
      </div>

      <Show when={q.isError}>
        <div class="rounded border border-danger bg-danger/10 p-3 text-[12px] text-danger">
          Couldn't load system status: {String(q.error)}
        </div>
      </Show>

      <Show when={s()} fallback={<div class="p-6 text-[12px] text-ink-700">Loading system status…</div>}>
        {/* Verdict banner */}
        <Show when={verdict()}>
          <div class="rounded-lg border p-3 text-[12px]"
               classList={{
                 "border-ok bg-ok/10 text-ok":          verdict()!.tone === "ok",
                 "border-warn bg-warn/10 text-warn":    verdict()!.tone === "warn",
                 "border-danger bg-danger/10 text-danger": verdict()!.tone === "danger",
               }}>
            <span class="font-semibold uppercase tracking-wider">{verdict()!.tone === "ok" ? "● healthy" : verdict()!.tone === "warn" ? "▲ attention" : "✕ fault"}</span>
            <span class="ml-2">{verdict()!.text}</span>
          </div>
        </Show>

        {/* KPI row */}
        <div class="flex flex-wrap gap-3">
          <Kpi label="Ingested today" value={s()!.ingest.studies_ingested_today}
               sub="files that arrived today (any procedure date)"
               tone={s()!.ingest.studies_ingested_today > 0 ? "ok" : "neutral"} />
          <Kpi label="Procedures today" value={s()!.ingest.procedures_today}
               sub="cases performed today (by study date) — matches worklist"
               tone={s()!.ingest.procedures_today > 0 ? "ok" : "neutral"} />
          <Kpi label="Instances / last hour" value={s()!.ingest.instances_last_hour} />
          <Kpi label="Last study sync" value={fmtAge(minutesSince(s()!.ingest.last_study_ingest))} sub={fmtTs(s()!.ingest.last_study_ingest)} />
          <Kpi label="Last edge check-in"
               value={fmtAge(s()!.edge.heartbeat_age_min)}
               sub="license ping (~30-day cadence) — not a live beacon"
               tone={s()!.edge.online ? "ok" : "warn"} />
          <Kpi label="Auth signing keys" value={s()!.auth.jwks_keys < 0 ? "?" : s()!.auth.jwks_keys} tone={s()!.auth.ok ? "ok" : "danger"} />
          <Kpi label="Database" value={s()!.db_ok ? "OK" : "DOWN"} tone={s()!.db_ok ? "ok" : "danger"} />
        </div>

        {/* Gauges + ingest chart */}
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div class="flex items-center justify-around rounded-lg border border-ink-300 bg-ink-100 p-4">
            <Gauge label="services up" value={servicesUp()} max={servicesTotal()}
                   tone={allUp() ? "ok" : servicesUp() >= servicesTotal() - 1 ? "warn" : "danger"} />
            <Gauge label="signing keys" value={Math.max(0, s()!.auth.jwks_keys)} max={4}
                   tone={s()!.auth.ok ? "ok" : "danger"} suffix="keys" />
          </div>
          <div class="rounded-lg border border-ink-300 bg-ink-100 p-4 lg:col-span-2">
            <div class="mb-2 text-[11px] uppercase tracking-wider text-ink-700">studies ingested · last 14 days</div>
            <IngestBars data={s()!.ingest.per_day} />
          </div>
        </div>

        {/* Service grid */}
        <div class="rounded-lg border border-ink-300 bg-ink-100 p-4">
          <div class="mb-2 text-[11px] uppercase tracking-wider text-ink-700">
            microservices · {servicesUp()}/{servicesTotal()} healthy
          </div>
          <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            <For each={s()!.services}>{(svc) => (
              <div class="flex items-center gap-2 rounded border border-ink-300 bg-ink-200 px-2.5 py-1.5">
                <span class="h-2.5 w-2.5 shrink-0 rounded-full"
                      classList={{ "bg-ok": svc.up, "bg-danger": !svc.up }} />
                <span class="truncate text-[12px] text-ink-900">{svc.name}</span>
                <span class="ml-auto font-mono text-[10px] text-ink-600">
                  {svc.up ? `${svc.latency_ms}ms` : (svc.status ? `HTTP ${svc.status}` : "down")}
                </span>
              </div>
            )}</For>
          </div>
        </div>

        {/* Edge activations */}
        <div class="rounded-lg border border-ink-300 bg-ink-100 p-4">
          <div class="mb-2 text-[11px] uppercase tracking-wider text-ink-700">endoedge activations</div>
          <Show when={s()!.edge.activations.length > 0}
                fallback={<div class="text-[12px] text-ink-600">No active endoedge boxes on record.</div>}>
            <table class="w-full text-[12px]">
              <thead class="text-left text-[10px] uppercase tracking-wider text-ink-700">
                <tr><th class="py-1">Box</th><th>Site</th><th>Lic</th><th>Version</th><th>Last seen</th><th class="text-right">Age</th></tr>
              </thead>
              <tbody class="divide-y divide-ink-300">
                <For each={s()!.edge.activations}>{(a) => (
                  <tr>
                    <td class="py-1 font-mono text-ink-900" title="activation id">{a.id}</td>
                    <td class="text-ink-900">{a.site || "—"}</td>
                    <td class="font-mono text-ink-700" title="license key prefix">{a.lic || "—"}</td>
                    <td class="text-ink-700">{a.version}</td>
                    <td class="text-ink-700">{fmtTs(a.last_seen)}</td>
                    <td class="text-right font-mono"
                        classList={{ "text-ok": a.age_min <= 60, "text-warn": a.age_min > 60 }}>
                      {fmtAge(a.age_min)}
                    </td>
                  </tr>
                )}</For>
              </tbody>
            </table>
          </Show>
        </div>

        {/* Studies uploaded, by box (last 7 days) — which room/box sent what. */}
        <div class="rounded-lg border border-ink-300 bg-ink-100 p-4">
          <div class="mb-2 text-[11px] uppercase tracking-wider text-ink-700">studies uploaded · by box (7 days)</div>
          <Show when={s()!.edge.uploads.length > 0}
                fallback={<div class="text-[12px] text-ink-600">No attributed uploads yet (per-study source is recorded from new uploads onward).</div>}>
            <table class="w-full text-[12px]">
              <thead class="text-left text-[10px] uppercase tracking-wider text-ink-700">
                <tr><th class="py-1">Box</th><th class="text-right">Today</th><th class="text-right">7&nbsp;days</th><th>Last upload</th></tr>
              </thead>
              <tbody class="divide-y divide-ink-300">
                <For each={s()!.edge.uploads}>{(u) => (
                  <tr>
                    <td class="py-1 text-ink-900">{u.site || `Box ${u.box}`}
                      <span class="ml-1.5 font-mono text-[10px] text-ink-700">{u.box}</span></td>
                    <td class="text-right font-mono text-ink-900">{u.today}</td>
                    <td class="text-right font-mono text-ink-700">{u.week}</td>
                    <td class="text-ink-700">{fmtTs(u.last_at)}</td>
                  </tr>
                )}</For>
              </tbody>
            </table>
          </Show>
        </div>
      </Show>
    </div>
  );
}

function minutesSince(ts: string | null): number | null {
  if (!ts) return null;
  const t = new Date(ts).getTime();
  if (Number.isNaN(t)) return null;
  return Math.round((Date.now() - t) / 60000);
}
